import AppIcon from '@shelflix/ui-web/components/AppIcon';
import { Dialog, DialogContent } from '@shelflix/ui-web/components/ui/dialog';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@shelflix/ui-web/components/ui/button';
import expiringSvg from '@/assets/icons-normalized/expiring.svg?raw';
import defaultSvg from '@/assets/icons-normalized/default.svg?raw';
import arrowRightSvg from '@/assets/icons-normalized/arrow-right.svg?raw';
import playSvg from '@/assets/icons-normalized/play.svg?raw';
import shelfTvSvg from '@/assets/icons-normalized/shelf-tv.svg?raw';
import alertSvg from '@/assets/icons-normalized/alert-triangle.svg?raw';
import saveSvg from '@/assets/icons-normalized/save.svg?raw';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { trpc } from '@/trpcClient';
import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { BatchActionFiltersFilterTypeWithValues } from '@shelflix/schemas/batchAction/filters';
import { CampaignDto } from '@shelflix/trpc/routes/batchAction/campaigns/query';
import { cn } from '@/utils/utils';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';
import { DateRange } from 'react-day-picker';
import Loader from '../Loader';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Checkbox } from '@shelflix/ui-web/components/ui/checkbox';
import { AlertModal } from '../AlertModal';
import { toast } from 'sonner';

interface BatchActionModalOptions {
  label: string;
  name: string;
  options: {
    name: string;
    value: string;
    photo?: string;
  }[];
  disabled?: boolean;
}

export function BatchActionModal({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) {
  const queryClient = useQueryClient();

  const defaultImage = 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png';
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [screen, setScreen] = useState<number>(0);
  const [stage, setStage] = useState<number>(0);
  const [filters, setFilters] = useState<BatchActionFiltersFilterTypeWithValues>({});
  const isBrandSelected = filters.filter?.some(
    (filter) => filter.field.name === 'brand' && filter.field.value !== 'nothing'
  );
  const isMaterialSelected = filters.filter?.some(
    (filter) => filter.field.name === 'material' && filter.field.value !== 'nothing'
  );
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | DateRange | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | DateRange | undefined>(undefined);
  const [password, setPassword] = useState<string>('');
  const [deleteSchedules, setDeleteSchedules] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const campaignScrollerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragScrollLeftRef = useRef(0);

  const resetModalState = () => {
    setSelectedAction(null);
    setScreen(0);
    setStage(0);
    setFilters({});
    setSelectedMaterial(null);
    setSelectedCampaign(null);
    setStartDate(undefined);
    setEndDate(undefined);
    setDeleteSchedules(false);
  };

  useEffect(() => {
    resetModalState();
  }, [open]);

  const handleCampaignMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const container = campaignScrollerRef.current;
    if (!container) return;

    isDraggingRef.current = true;
    dragStartXRef.current = event.pageX - container.offsetLeft;
    dragScrollLeftRef.current = container.scrollLeft;
  };

  const handleCampaignMouseLeave = () => {
    isDraggingRef.current = false;
  };

  const handleCampaignMouseUp = () => {
    isDraggingRef.current = false;
  };

  const handleCampaignMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    const container = campaignScrollerRef.current;
    if (!container) return;

    event.preventDefault();
    const currentX = event.pageX - container.offsetLeft;
    const distance = currentX - dragStartXRef.current;
    container.scrollLeft = dragScrollLeftRef.current - distance;
  };

  const actions = [
    {
      label: 'Agendamentos',
      value: 'agendamentos',
      icon: expiringSvg,
    },
    {
      label: 'Padrão',
      value: 'padrao',
      icon: defaultSvg,
    },
  ];

  const header = [
    {
      label: 'Material',
      icon: shelfTvSvg,
      value: 0,
    },
    {
      label: 'Filtro',
      icon: playSvg,
      value: 1,
      disabled: !(isBrandSelected && isMaterialSelected),
    },
    {
      label: 'Confirmação',
      icon: playSvg,
      value: 2,
      disabled: selectedAction === 'agendamentos' ? !selectedCampaign || !startDate || !endDate : !selectedCampaign,
    },
  ];

  const filterToLabel = {
    brand: 'Marca',
    material: 'Tipo de Material',
    state: 'UF',
    city: 'Cidade',
    tags: 'Tags',
  };

  const {
    data: filterOptions = {
      brands: [{ name: '', value: 'nothing' }],
      materials: [{ name: '', value: 'nothing', photo: '' }],
      states: [{ name: '', value: 'nothing' }],
      cities: [{ name: '', value: 'nothing' }],
      tags: [{ name: '', value: 'nothing' }],
    },
  } = useQuery({
    ...trpc.batchAction.filters.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  type FilterName = 'brand' | 'material' | 'state' | 'city' | 'tags';

  const updateFilter = ({ name, value, photo }: { name: FilterName; value: string; photo?: string }) => {
    if (name === 'material') {
      setSelectedMaterial(photo || null);
    } else if (name === 'state' && value === 'nothing') {
      updateFilter({ name: 'city', value: 'nothing' });
    }

    setFilters((prev) => {
      const next = prev.filter ? [...prev.filter] : [];
      const index = next.findIndex((item) => item.field.name === name);

      if (!value) {
        if (index >= 0) {
          next.splice(index, 1);
        }
      } else if (value !== 'nothing') {
        const entry = { field: { name, condition: 'equals', value } } as const;
        if (index >= 0) {
          next[index] = entry;
        } else {
          next.push(entry);
        }
      } else {
        if (index >= 0) {
          next.splice(index, 1);
        }
      }

      return { ...prev, filter: next.length > 0 ? next : undefined };
    });
  };

  const inputs: BatchActionModalOptions[] = [
    {
      label: 'Marca',
      name: 'brand' as const,
      options: filterOptions.brands,
    },
    {
      label: 'Tipo de Material',
      name: 'material' as const,
      options: filterOptions.materials,
    },
    {
      label: 'UF',
      name: 'state' as const,
      options: filterOptions.states,
      disabled: filters.filter?.some((filter) => filter.field.name === 'tags'),
    },
    {
      label: 'Cidade',
      name: 'city' as const,
      options: filterOptions.cities,
      disabled: !filters.filter?.some((filter) => filter.field.name === 'state'),
    },
    {
      label: 'Tag',
      name: 'tags' as const,
      options: filterOptions.tags,
      disabled: filters.filter?.some((filter) => filter.field.name === 'state'),
    },
  ];

  const { data: campaignsData = [] } = useQuery({
    ...trpc.batchAction.campaigns.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    enabled: stage === 1,
    placeholderData: keepPreviousData,
  });

  const { data: affectedMaterialsCount = [] } = useQuery({
    ...trpc.batchAction.affectedMaterials.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    enabled: stage === 2,
  });

  const normalizedCampaigns: CampaignDto[] = campaignsData.map((campaign) => ({
    ...campaign,
    thumbnailUrl: campaign.thumbnailUrl ?? null,
    campaignMedias: campaign.campaignMedias.map((media) => ({
      ...media,
      thumbnailUrl: media.thumbnailUrl ?? null,
    })),
  }));

  const displayFilterValue = (name: FilterName, value: string | undefined) => {
    switch (name) {
      case 'state':
        return filterOptions.states.find((s) => s.value === value)?.name;
      case 'city':
        return filterOptions.cities.find((c) => c.value === value)?.name;
      case 'brand':
        return filterOptions.brands.find((b) => b.value === value)?.name;
      case 'material':
        return filterOptions.materials.find((m) => m.value === value)?.name;
      case 'tags':
        return filterOptions.tags.find((t) => t.value === value)?.name;
      default:
        return undefined;
    }
  };

  const replaceCampaignMutation = useMutation(trpc.batchAction.replaceCampaign.mutationOptions());
  const handleBatchAction = async () => {
    setShowAlert(false);
    if (selectedCampaign === null) {
      toast.error('Selecione uma campanha para continuar.');
      return;
    }

    try {
      const data = await replaceCampaignMutation.mutateAsync({
        materialUuids: affectedMaterialsCount.map((material) => material.uuid),
        password,
        campaign: {
          id: selectedCampaign,
          isDefault: selectedAction === 'padrao',
          deleteSchedules: deleteSchedules,
          startDate:
            selectedAction === 'agendamentos' && startDate instanceof Date ? startDate.toISOString() : undefined,
          endDate: selectedAction === 'agendamentos' && endDate instanceof Date ? endDate.toISOString() : undefined,
        },
        brandUuid: filters.filter?.find((filter) => filter.field.name === 'brand')?.field.value,
        materialTypeUuid: filters.filter?.find((filter) => filter.field.name === 'material')?.field.value,
        stateUuid: filters.filter?.find((filter) => filter.field.name === 'state')?.field.value,
        cityUuid: filters.filter?.find((filter) => filter.field.name === 'city')?.field.value,
        tagUuid: filters.filter?.find((filter) => filter.field.name === 'tags')?.field.value,
      });

      queryClient.invalidateQueries(trpc.material.get.pathFilter());

      resetModalState();
      setOpen(false);
      return data;
    } catch (err: unknown) {
      const unauthorizedError =
        typeof err === 'object' &&
        err !== null &&
        'data' in err &&
        typeof (err as { data?: { code?: string } }).data?.code === 'string' &&
        (err as { data?: { code?: string } }).data?.code === 'UNAUTHORIZED';

      if (unauthorizedError) {
        toast.error('Senha incorreta. Por favor, tente novamente.');
      } else {
        toast.error('Ocorreu um erro ao processar a ação em lote. Por favor, tente novamente.');
      }
    }
  };

  return (
    <>
      <AlertModal
        openModal={showAlert}
        onOpenChange={setShowAlert}
        icon={
          <AppIcon
            source="svg"
            name="warning"
            svgContent={alertSvg}
            size={32}
            weight={400}
            colorStyle={{ type: 'solid', color: 'var(--primary)' }}
            className="p-2"
          />
        }
        title="Alerta importante!"
        subtitle="Confirmação de agendamento em lote"
        content={
          <div className="mb-4 flex items-center justify-center">
            <p className="text-center text-[16px] font-semibold text-[#DC3545]">
              Atenção: ao confirmar este agendamento, a nova campanha substituirá as campanhas atualmente ativas nos
              materiais afetados caso exista conflito entre as datas e horários programados. Deseja continuar?
            </p>
          </div>
        }
        actionBotton={
          <div className="flex flex-row items-center gap-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowAlert(false)}>
              Cancelar
            </Button>
            <Button variant="default" className="flex-1" onClick={handleBatchAction}>
              Continuar
            </Button>
          </div>
        }
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="h-full max-h-[calc(100vh-1rem)] w-full bg-[#0E0E0E] p-6">
          {screen === 0 ? (
            <div className="flex h-full flex-col gap-3">
              <div className="flex h-fit flex-col items-center gap-2">
                <AppIcon
                  source="material-symbols"
                  name="stacks"
                  size={40}
                  weight={400}
                  colorStyle={{ type: 'solid', color: 'var(--primary)' }}
                  className="rounded-[10px] bg-[var(--primary)]/10 p-5"
                />
                <h1 className="text-primary text-[24px] font-semibold">Ações em lote</h1>
              </div>
              <div>
                <p className="mb-3 text-[16px] text-[#F2E6DA]">
                  Selecione a configuração de tipos de campanha abaixo, antes de prosseguir:
                </p>
                <ul className="list-inside list-disc pl-4 text-[16px] text-[#F2E6DA]">
                  <li className="mb-3">
                    <strong>Agendamentos</strong> → Campanhas com data de inicio e fim, com horários opcionais de
                    veiculação;
                  </li>
                  <li>
                    <strong>Padrão</strong> → Campanhas que serão transmitidas quando não houver nenhum outro
                    agendamento configurado.
                  </li>
                </ul>
              </div>
              <div className="flex flex-1 flex-row items-center justify-center gap-6">
                {actions.map((action) => (
                  <button
                    className={`flex h-45 w-45 cursor-pointer flex-col items-center justify-center gap-4 rounded-[10px] border ${
                      selectedAction === action.value
                        ? 'border-primary text-primary bg-[var(--primary)]/10'
                        : 'border-secondary bg-[#0E0E0E] text-[#736A61]'
                    }`}
                    onClick={() => setSelectedAction(action.value)}
                  >
                    <AppIcon
                      source="svg"
                      name="edit_square"
                      svgContent={action.icon}
                      size={50}
                      weight={400}
                      colorStyle={{
                        type: 'solid',
                        color: selectedAction === action.value ? 'var(--primary)' : '#736A61',
                      }}
                    />
                    {action.label}
                  </button>
                ))}
              </div>
              <div className="border-secondary mt-auto w-full border-t-2 pt-3">
                <Button variant="default" className="w-full" disabled={!selectedAction} onClick={() => setScreen(1)}>
                  Iniciar configurações
                  <AppIcon
                    source="svg"
                    name="arrow_right"
                    svgContent={arrowRightSvg}
                    size={28}
                    weight={400}
                    colorStyle={{ type: 'solid', color: '#FFF9F2' }}
                    className="size-7"
                  />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-w-full flex-col">
              {/* Header */}
              <div className="mb-6 flex flex-row items-center gap-6">
                {header.map((item) => (
                  <div
                    className={cn(
                      'flex w-full flex-col gap-1',
                      item.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    )}
                    onClick={() => {
                      if (item.disabled) return;
                      setStage(item.value);
                    }}
                  >
                    <div className="flex flex-row items-center gap-1">
                      <AppIcon
                        source="svg"
                        name={item.label}
                        svgContent={item.icon}
                        size={18}
                        weight={400}
                        colorStyle={{ type: 'solid', color: stage >= item.value ? 'var(--primary)' : '#736A61' }}
                        className="p-1"
                      />
                      <p className={`text-[10px] ${stage >= item.value ? 'text-primary' : 'text-[#736A61]'}`}>
                        {item.label}
                      </p>
                    </div>
                    <div
                      className={`h-3 w-full rounded-[20px] ${stage >= item.value ? 'bg-[var(--primary)]/10' : 'bg-[#191919]'} `}
                    ></div>
                  </div>
                ))}
              </div>

              {/* Content */}
              <div className="flex h-full w-full flex-row overflow-hidden">
                <div
                  className="flex h-full w-full flex-row gap-12 transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(calc(-${stage * 100}% - ${stage * 48}px))` }}
                >
                  {/* Stage 1 */}
                  <div className="flex w-full shrink-0 flex-col gap-4">
                    <p className="mb-2 text-[12px] text-[#F2E6DA]">
                      Edite a campanha em multiplos dispositivos segmentando por Estado, Tags, Marca e Tipo de Material
                    </p>
                    {inputs.map((input) => (
                      <div className="flex w-full shrink-0 flex-row items-center gap-4" key={input.name}>
                        <div className="flex w-full flex-col gap-1">
                          <p className="text-[12px] text-[#F2E6DA]">{input.label}</p>
                          <Select
                            onValueChange={(val) =>
                              updateFilter({
                                name: input.name as FilterName,
                                value: val,
                                photo: input.options.find((o) => o.value === val)?.photo,
                              })
                            }
                            value={
                              filters.filter?.find((filter) => filter.field.name === input.name)?.field.value ||
                              'nothing'
                            }
                            disabled={input.disabled}
                            defaultValue="nothing"
                          >
                            <SelectTrigger className="w-full cursor-pointer rounded-[5px] border-[#2e2b33] bg-[#191919] disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#2e2b33] dark:bg-[#191919] [&>svg]:text-[#B2A598]! [&>svg]:opacity-100">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nothing">Selecione</SelectItem>
                              {input.options.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {input.name === 'material' && (
                          <>
                            {selectedMaterial ? (
                              <img
                                src={selectedMaterial || defaultImage}
                                alt={input.label}
                                className="aspect-square h-[100px] w-[100px] rounded-[10px] bg-black object-contain"
                              />
                            ) : (
                              <span className="aspect-square h-[100px] w-[100px] rounded-[10px] bg-black" />
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    <div className="border-secondary mt-auto w-full border-t-2 pt-3">
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => setStage(stage + 1)}
                        disabled={!(isBrandSelected && isMaterialSelected)}
                      >
                        Próximo passo
                        <AppIcon
                          source="svg"
                          name="arrow_right"
                          svgContent={arrowRightSvg}
                          size={28}
                          weight={400}
                          colorStyle={{ type: 'solid', color: '#FFF9F2' }}
                          className="size-7"
                        />
                      </Button>
                    </div>
                  </div>

                  {/* Stage 2 */}
                  <div className="flex w-full shrink-0 flex-col">
                    <p className="text-[12px] text-[#F2E6DA]">
                      Edite a campanha em multiplos dispositivos segmentando por Estado, Tags, Marca e Tipo de Material
                    </p>
                    <div
                      ref={campaignScrollerRef}
                      className="flex min-h-[252px] flex-row gap-2.5 overflow-x-auto px-3 py-9 select-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                      onMouseDown={handleCampaignMouseDown}
                      onMouseLeave={handleCampaignMouseLeave}
                      onMouseUp={handleCampaignMouseUp}
                      onMouseMove={handleCampaignMouseMove}
                    >
                      {normalizedCampaigns.length === 0 && <Loader />}
                      {normalizedCampaigns.map((campaign, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            'hover:border-primary relative h-[180px] w-[100px] shrink-0 cursor-pointer overflow-hidden rounded-[5px] hover:border hover:shadow-[0px_0px_10px_0px_#E57300]',
                            selectedCampaign === campaign.id
                              ? 'border-primary border shadow-[0px_0px_10px_0px_#E57300]'
                              : 'border border-transparent'
                          )}
                          onClick={() => setSelectedCampaign(campaign.id)}
                        >
                          <img
                            src={campaign.thumbnailUrl || defaultImage}
                            alt={campaign.name}
                            className="h-full w-full rounded-b-[6px] object-cover"
                          />
                          <div
                            className="absolute inset-0"
                            style={{
                              background: 'linear-gradient(179.86deg, rgba(20, 20, 20, 0) 0.12%, #0F0E0E 80.7%)',
                            }}
                          >
                            <p
                              className="absolute right-2 bottom-2 left-2 text-[10px] font-semibold text-[#F2E6DA]"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {campaign.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedAction === 'agendamentos' ? (
                      <div className="flex flex-col gap-6">
                        <p className="text-[12px] text-[#F2E6DA]">
                          Edite a campanha em multiplos dispositivos segmentando por Estado, Tags, Marca e Tipo de
                          Material
                        </p>
                        <div className="flex w-full flex-col gap-1">
                          <p className="text-[12px] text-[#F2E6DA]">Data de inicio</p>
                          <DatePicker
                            mode="single"
                            value={startDate}
                            onChange={setStartDate}
                            disabled={!selectedCampaign}
                          />
                        </div>
                        <div className="flex w-full flex-col gap-1">
                          <p className="text-[12px] text-[#F2E6DA]">Data de encerramento</p>
                          <DatePicker
                            mode="single"
                            value={endDate}
                            onChange={setEndDate}
                            disabled={!selectedCampaign}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex w-full flex-row items-center gap-2">
                        <Checkbox
                          checked={deleteSchedules}
                          onCheckedChange={(checked) => setDeleteSchedules(!!checked)}
                        />
                        <p className="text-[14px] text-[#F2E6DA]">Excluir agendamentos existentes</p>
                      </div>
                    )}

                    <div className="border-secondary mt-auto w-full border-t-2 pt-3">
                      <Button
                        variant="default"
                        className="w-full"
                        disabled={
                          selectedAction === 'agendamentos'
                            ? !selectedCampaign || !startDate || !endDate
                            : !selectedCampaign
                        }
                        onClick={() => setStage(stage + 1)}
                      >
                        Próximo passo
                        <AppIcon
                          source="svg"
                          name="arrow_right"
                          svgContent={arrowRightSvg}
                          size={28}
                          weight={400}
                          colorStyle={{ type: 'solid', color: '#FFF9F2' }}
                          className="size-7"
                        />
                      </Button>
                    </div>
                  </div>

                  {/* Stage 3 */}
                  <div className="flex w-full shrink-0 flex-col gap-4">
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex w-full flex-row items-center justify-center">
                        <AppIcon
                          source="svg"
                          name="warning"
                          svgContent={alertSvg}
                          size={32}
                          weight={400}
                          colorStyle={{ type: 'solid', color: 'var(--primary)' }}
                          className="p-2"
                        />
                        <h1 className="text-primary text-[20px]">Atenção</h1>
                      </div>
                      <p className="text-[12px] text-[#F2E6DA]">
                        A campanha de ação em lote irá sobrescrever as campanhas em veiculação nos materiais.
                      </p>
                    </div>

                    <div className="flex w-full flex-col gap-1">
                      {filters.filter?.map((filter, idx) => (
                        <div className="flex w-full flex-row items-center justify-between" key={idx}>
                          <p className="text-primary text-[12px] font-bold">
                            {filterToLabel[filter.field.name] || filter.field.name}
                          </p>
                          <p className="text-[12px] text-[#FFF9F2]">
                            {displayFilterValue(filter.field.name, filter.field.value)}
                          </p>
                        </div>
                      ))}
                      <div className="flex w-full flex-row items-center justify-between">
                        <p className="text-primary text-[12px] font-bold">Materias Afetados</p>
                        <p className="text-[12px] text-[#FFF9F2]">{affectedMaterialsCount.length}</p>
                      </div>
                    </div>

                    <p className="text-[12px] text-[#F2E6DA]">Digite sua senha abaixo para confirmar esta ação.</p>

                    <Input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Digite sua senha"
                      type="password"
                    />

                    <div className="border-secondary mt-auto w-full border-t-2 pt-3">
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={selectedAction === 'agendamentos' ? () => setShowAlert(true) : handleBatchAction}
                        disabled={replaceCampaignMutation.isPending || password.length === 0}
                      >
                        <AppIcon
                          source="svg"
                          name="save"
                          svgContent={saveSvg}
                          size={28}
                          weight={400}
                          colorStyle={{ type: 'solid', color: '#FFF9F2' }}
                          className="size-7"
                        />
                        Finalizar ação
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
